// @givmo/board — the officer's reads of the board's self-assessment, each dark until the backend
// serves it (CBG-YR4-ASSESS-WEB; the annual disclosures' shape, `conflicts/coi-queries.ts`).
//
// ══ A ROUTE MISS IS "NOT SERVED", NEVER AN ERROR ════════════════════════════════════════════════
//
// While `BOARD_SELF_ASSESSMENT_ENABLED` is off every self-assessment route answers the absent-route
// 404, and the list read here RESOLVES that to `null` — a settled answer meaning "not served", so the
// Evaluations page says one quiet line and nothing retries. Any other failure stays an error. There is
// no web flag: the backend's answer is the gate.
//
// One key family, `boardAssessmentKeys`, so one act refreshes every read of its organization's
// self-assessments — and the minute book, which the close adds a record to.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BoardSelfAssessmentInstrumentOut,
  BoardSelfAssessmentOut,
  BoardSelfAssessmentQuestionsOut,
  BoardSelfAssessmentResultsOut,
  BoardSelfAssessmentSummaryOut,
} from '@givmo/api-client';
import { hasBoardPermission } from '../../board-host';
import { BOARD_READ_PERMISSION } from '../../board-routes';
import { useBoardOperations, useBoardStanding } from '../../board-runtime';
import { isNotServed } from '../../lib/not-served';
import {
  decodeAssessment,
  decodeInstrument,
  decodeQuestions,
  decodeResults,
  extractAssessments,
  extractInstruments,
} from './assessment-decode';

/** The self-assessment's query keys, rooted per organization so one act can refresh them all. */
export function boardAssessmentKeys(orgRef: string | null) {
  const root = ['board-self-assessment', orgRef ?? ''] as const;
  return {
    root,
    list: [...root, 'list'] as const,
    one: (assessmentId: string) => [...root, 'one', assessmentId] as const,
    questions: (assessmentId: string) => [...root, 'questions', assessmentId] as const,
    results: (assessmentId: string) => [...root, 'results', assessmentId] as const,
    instrument: [...root, 'instrument'] as const,
  };
}

/** Never retry the route miss: it is an answer, not a blip. */
function retryUnlessNotServed(failureCount: number, error: unknown): boolean {
  return !isNotServed(error) && failureCount < 1;
}

/**
 * Every self-assessment of this organization, newest first: `null` while the self-assessment is not
 * served here (its route miss), the list once it is.
 */
export function useSelfAssessments(orgRef: string | null, enabled = true) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardAssessmentKeys(orgRef).list,
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardSelfAssessmentSummaryOut[] | null> => {
      try {
        const { data } = await operations.officerListBoardSelfAssessments({
          path: { org_ref: orgRef ?? '' },
          throwOnError: true,
        });
        return extractAssessments(data);
      } catch (error) {
        if (isNotServed(error)) return null;
        throw error;
      }
    },
  });
}

/**
 * Whether the self-assessment is served here, for its menu row (Org Machinery OM-99's `useServed`):
 * `true` once the list read answers, `false` once it is the route miss — not served here
 * (`BOARD_SELF_ASSESSMENT_ENABLED` off, dark in every environment since CBG-YR4-ASSESS-DARK) — and
 * `undefined` while it is read or after a failed read, so the shell draws the row only once it is
 * known served. The page's own cache entry, keyed through the host's standing hook (the menu draws
 * outside Board's officer host), so the row and the page never disagree. Read only once the standing
 * has settled on the list read's own string (the counts' rule): before it, a row is a plain link for
 * a moment, and a gated module (off, locked, setting up) must read nothing of Board.
 */
export function useSelfAssessmentServed(): boolean | undefined {
  const { orgRef, permissions } = useBoardStanding();
  const allowed = hasBoardPermission(permissions, BOARD_READ_PERMISSION);
  const read = useSelfAssessments(orgRef, allowed);
  if (!allowed) return undefined;
  return read.data === undefined ? undefined : read.data !== null;
}

/** One assessment's dashboard: who was asked and who has answered, never what (§2.4). */
export function useSelfAssessment(orgRef: string | null, assessmentId: string | null) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardAssessmentKeys(orgRef).one(assessmentId ?? ''),
    enabled: orgRef !== null && assessmentId !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardSelfAssessmentOut | null> => {
      const { data } = await operations.officerGetBoardSelfAssessment({
        path: { org_ref: orgRef ?? '', assessment_id: assessmentId ?? '' },
        throwOnError: true,
      });
      return decodeAssessment(data);
    },
  });
}

/** A closed one's results: the record's payload, with the board's priorities (§9). */
export function useSelfAssessmentResults(
  orgRef: string | null,
  assessmentId: string | null,
  enabled = true,
) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardAssessmentKeys(orgRef).results(assessmentId ?? ''),
    enabled: enabled && orgRef !== null && assessmentId !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardSelfAssessmentResultsOut | null> => {
      const { data } = await operations.officerGetBoardSelfAssessmentResults({
        path: { org_ref: orgRef ?? '', assessment_id: assessmentId ?? '' },
        throwOnError: true,
      });
      return decodeResults(data);
    },
  });
}

/**
 * The questions as an assessment asks them, frozen at its opening: what a paper sheet prints and
 * enters. Read fresh each time a sheet is opened.
 */
export function useSelfAssessmentQuestions(
  orgRef: string | null,
  assessmentId: string | null,
  enabled = true,
) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardAssessmentKeys(orgRef).questions(assessmentId ?? ''),
    enabled: enabled && orgRef !== null && assessmentId !== null,
    retry: retryUnlessNotServed,
    staleTime: 0,
    queryFn: async (): Promise<BoardSelfAssessmentQuestionsOut | null> => {
      const { data } = await operations.officerGetBoardSelfAssessmentQuestions({
        path: { org_ref: orgRef ?? '', assessment_id: assessmentId ?? '' },
        throwOnError: true,
      });
      return decodeQuestions(data);
    },
  });
}

/**
 * Givmo's starting instrument offered to this organization, with its statements (the newest): `null`
 * where none is offered for the organization's type, until GivFi Operations supplies one (§3.1).
 */
export function useStartingInstrument(orgRef: string | null, enabled = true) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardAssessmentKeys(orgRef).instrument,
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardSelfAssessmentInstrumentOut | null> => {
      const { data: list } = await operations.officerListBoardSelfAssessmentInstruments({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      const newest = [...extractInstruments(list)].sort((a, b) => b.version - a.version)[0];
      if (newest === undefined) return null;
      const { data } = await operations.officerGetBoardSelfAssessmentInstrument({
        path: { org_ref: orgRef ?? '', template_id: newest.template_id },
        throwOnError: true,
      });
      return decodeInstrument(data);
    },
  });
}

/** Refresh every read of the self-assessment after an act, and the minute book the close writes to. */
export function useRefreshSelfAssessments(orgRef: string | null) {
  const queryClient = useQueryClient();
  const operations = useBoardOperations();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: boardAssessmentKeys(orgRef).root }),
      // The generated key without a `query` member is the family's prefix, so every filtered read of
      // the minute book refreshes (the register's precedent, `useRefreshCoi`).
      queryClient.invalidateQueries({
        queryKey: operations.officerListBoardRecordsOptions({ path: { org_ref: orgRef ?? '' } })
          .queryKey,
      }),
    ]);
  };
}
